import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyEditWebApiHandler_hasDeckOutDoorCommonArea,
  useRentalManagerUserPropertyEditWebApiHandler_hasGardenOutDoorCommonArea,
  useRentalManagerUserPropertyEditWebApiHandler_hasGazeboOutDoorCommonArea,
  useRentalManagerUserPropertyEditWebApiHandler_hasLawnOutDoorCommonArea,
  useRentalManagerUserPropertyEditWebApiHandler_hasPatioOutDoorCommonArea,
  useRentalManagerUserPropertyEditWebApiHandler_hasPicnicAreaOutDoorCommonArea,
  useRentalManagerUserPropertyEditWebApiHandler_hasPlaygroundOutDoorCommonArea,
  useRentalManagerUserPropertyEditWebApiHandler_hasRooftopDeckOutDoorCommonArea,
  useRentalManagerUserPropertyEditWebApiHandler_hasTrailOutDoorCommonArea,
  useRentalManagerUserPropertyEditWebApiHandler_hasWaterFeatureOutDoorCommonArea,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import {
  OUTDOOR_AREA_KEYS,
  OUTDOOR_AREA_METADATA,
  type OutdoorAreaKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<OutdoorAreaKey, boolean>;

const OutdoorAreasAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const outdoorCommonAreaTypes = listingDetails?.buildingAttributes?.outdoorCommonAreaTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasDeck: outdoorCommonAreaTypes?.hasDeck ?? false,
    hasGarden: outdoorCommonAreaTypes?.hasGarden ?? false,
    hasGazebo: outdoorCommonAreaTypes?.hasGazebo ?? false,
    hasLawn: outdoorCommonAreaTypes?.hasLawn ?? false,
    hasPatio: outdoorCommonAreaTypes?.hasPatio ?? false,
    hasPicnicArea: outdoorCommonAreaTypes?.hasPicnicArea ?? false,
    hasPlayground: outdoorCommonAreaTypes?.hasPlayground ?? false,
    hasRooftopDeck: outdoorCommonAreaTypes?.hasRooftopDeck ?? false,
    hasTrail: outdoorCommonAreaTypes?.hasTrail ?? false,
    hasWaterFeature: outdoorCommonAreaTypes?.hasWaterFeature ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Outdoor common areas updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateDeck } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasDeckOutDoorCommonArea,
    skipToast: true,
    meta: { context: { api: 'hasDeckOutDoorCommonArea', details: { listingAlias } } },
  });

  const { mutateAsync: updateGarden } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasGardenOutDoorCommonArea,
    skipToast: true,
    meta: { context: { api: 'hasGardenOutDoorCommonArea', details: { listingAlias } } },
  });

  const { mutateAsync: updateGazebo } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasGazeboOutDoorCommonArea,
    skipToast: true,
    meta: { context: { api: 'hasGazeboOutDoorCommonArea', details: { listingAlias } } },
  });

  const { mutateAsync: updateLawn } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasLawnOutDoorCommonArea,
    skipToast: true,
    meta: { context: { api: 'hasLawnOutDoorCommonArea', details: { listingAlias } } },
  });

  const { mutateAsync: updatePatio } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasPatioOutDoorCommonArea,
    skipToast: true,
    meta: { context: { api: 'hasPatioOutDoorCommonArea', details: { listingAlias } } },
  });

  const { mutateAsync: updatePicnicArea } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasPicnicAreaOutDoorCommonArea,
    skipToast: true,
    meta: { context: { api: 'hasPicnicAreaOutDoorCommonArea', details: { listingAlias } } },
  });

  const { mutateAsync: updatePlayground } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasPlaygroundOutDoorCommonArea,
    skipToast: true,
    meta: { context: { api: 'hasPlaygroundOutDoorCommonArea', details: { listingAlias } } },
  });

  const { mutateAsync: updateRooftopDeck } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasRooftopDeckOutDoorCommonArea,
    skipToast: true,
    meta: { context: { api: 'hasRooftopDeckOutDoorCommonArea', details: { listingAlias } } },
  });

  const { mutateAsync: updateTrail } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasTrailOutDoorCommonArea,
    skipToast: true,
    meta: { context: { api: 'hasTrailOutDoorCommonArea', details: { listingAlias } } },
  });

  const { mutateAsync: updateWaterFeature } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasWaterFeatureOutDoorCommonArea,
    skipToast: true,
    meta: { context: { api: 'hasWaterFeatureOutDoorCommonArea', details: { listingAlias } } },
  });

  const mutationMap: Record<keyof LocalState, typeof updateDeck> = {
    hasDeck: updateDeck,
    hasGarden: updateGarden,
    hasGazebo: updateGazebo,
    hasLawn: updateLawn,
    hasPatio: updatePatio,
    hasPicnicArea: updatePicnicArea,
    hasPlayground: updatePlayground,
    hasRooftopDeck: updateRooftopDeck,
    hasTrail: updateTrail,
    hasWaterFeature: updateWaterFeature,
  };

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as OutdoorAreaKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = OUTDOOR_AREA_METADATA[fieldName];
      await mutationMap[fieldName]({
        getParams: {
          [metadata.apiKey]: checked,
          listingAlias,
        },
      });
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Outdoor common areas</Legend>} disabled={isInputFormDisabled}>
      {OUTDOOR_AREA_KEYS.map((fieldName) => {
        const metadata = OUTDOOR_AREA_METADATA[fieldName];
        return (
          <LabeledControl
            key={`outdoorCommonAreaTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(OutdoorAreasAmenities);
